import React, { useEffect, useState, useRef, useMemo } from "react";
import {
  Hotel, CreditCard, Unlock, Receipt, Monitor, QrCode, Smartphone, Route,
  Filter, Star, BarChart2, PieChart, Calculator, Target, Info, SlidersHorizontal,
  Database, LayoutGrid, Download, Wifi, DoorOpen, Lock, CalendarCheck2,
  PencilRuler, Newspaper, Gem, HelpCircle, BookOpen, Crown, FileSignature,
  Gamepad2, UserCog, Search, ArrowRight, Mail, Ticket, Percent, MapPin,
  MessageSquare, Code, Users, Timer
} from "lucide-react";

import { isFeatureLocked } from "../utils/permissions";

/**
 * Command Palette - Modern Cmd+K style quick navigation
 * Allows users to quickly search and navigate to any section
 */
const CommandPalette = ({ isOpen, onClose, role, type, category, canAccess, hasAccessToAny, push }) => {
  const [search, setSearch] = useState("");
  const [proToast, setProToast] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef(null);

  // All available commands with their metadata
  const allCommands = useMemo(() => {
    const commands = [];

    // Acciones
    if (canAccess(role, type, category, 'montarto')) {
      commands.push({
        id: 'montarto',
        label: 'Montarto',
        description: 'Gestión de alquiler de lockers',
        category: 'Acciones',
        icon: Hotel,
        path: '/hotelrental',
        keywords: ['hotel', 'rental', 'alquiler', 'montarto']
      });
    }
    if (canAccess(role, type, category, 'lockersale')) {
      commands.push({
        id: 'vender',
        label: 'Vender',
        description: 'Venta de lockers',
        category: 'Acciones',
        icon: CreditCard,
        path: '/lockersale',
        keywords: ['venta', 'vender', 'sale', 'pago']
      });
    }
    if (canAccess(role, type, category, 'pin')) {
      commands.push({
        id: 'recuperar',
        label: 'Recuperar PIN',
        description: 'Recuperación de PIN de locker',
        category: 'Acciones',
        icon: Unlock,
        path: '/pin',
        keywords: ['pin', 'recuperar', 'unlock', 'desbloquear']
      });
    }
    if (canAccess(role, type, category, 'supportticketadd')) {
      commands.push({
        id: 'ticket',
        label: 'Ticket de Soporte',
        description: 'Crear nuevo ticket de soporte',
        category: 'Acciones',
        icon: Receipt,
        path: '/supportticketadd',
        keywords: ['ticket', 'soporte', 'support', 'ayuda']
      });
    }

    // Canales
    if (canAccess(role, type, category, 'machines')) {
      commands.push({
        id: 'machines',
        label: category === 'BAQUEIRA' ? 'Controladores' : 'Kioscos',
        description: 'Gestión de máquinas y kioscos',
        category: 'Canales',
        icon: Monitor,
        path: '/machines',
        keywords: ['kiosco', 'maquina', 'machine', 'controlador']
      });
    }
    if (canAccess(role, type, category, 'qrs')) {
      commands.push({
        id: 'qrs',
        label: 'QRs',
        description: 'Gestión de códigos QR',
        category: 'Canales',
        icon: QrCode,
        path: '/qrs',
        keywords: ['qr', 'codigo', 'code']
      });
      commands.push({
        id: 'pdas',
        label: 'PDAs',
        description: 'Gestión de dispositivos PDA',
        category: 'Canales',
        icon: Smartphone,
        path: '/pdas',
        keywords: ['pda', 'dispositivo', 'mobile']
      });
    }
    if (canAccess(role, type, category, 'operators')) {
      commands.push({
        id: 'operators',
        label: 'Agregadores',
        description: 'Gestión de operadores agregadores',
        category: 'Baqueira',
        icon: Route,
        path: '/operators',
        keywords: ['agregador', 'operator', 'route']
      });
    }

    // Gestión
    if (canAccess(role, type, category, 'redeemcodes')) {
      commands.push({
        id: 'redeemcodes',
        label: 'Redeem Codes',
        description: 'Gestión de códigos de redención',
        category: 'Gestión',
        icon: Ticket,
        path: '/redeemcodes',
        keywords: ['redeem', 'codigo', 'code', 'vale']
      });
    }
    if (canAccess(role, type, category, 'email-templates')) {
      commands.push({
        id: 'email-templates',
        label: 'Plantillas de Email',
        description: 'Plantillas de correo electrónico',
        category: 'Gestión',
        icon: Mail,
        path: '/email-templates',
        keywords: ['email', 'plantilla', 'template', 'correo']
      });
    }
    if (canAccess(role, type, category, 'partners')) {
      commands.push({
        id: 'partners',
        label: 'Partners',
        description: 'Gestión de partners y colaboradores',
        category: 'Gestión',
        icon: Users,
        path: '/partners',
        keywords: ['partner', 'colaborador', 'socio']
      });
    }
    if (canAccess(role, type, category, 'discount-codes')) {
      commands.push({
        id: 'discount-codes',
        label: 'Códigos de Descuento',
        description: 'Gestión de códigos de descuento',
        category: 'Gestión',
        icon: Percent,
        path: '/discount-codes',
        keywords: ['descuento', 'discount', 'codigo', 'promocion']
      });
    }

    // Reporte
    if (canAccess(role, type, category, 'sales')) {
      commands.push({
        id: 'sales',
        label: 'Ventas',
        description: 'Reporte de ventas',
        category: 'Reporte',
        icon: Filter,
        path: '/sales',
        keywords: ['venta', 'sale', 'reporte', 'informe']
      });
    }
    if (canAccess(role, type, category, 'duplicated-sales')) {
      commands.push({
        id: 'duplicated-sales',
        label: 'Ventas Duplicadas',
        description: 'Ventas con Code duplicado (contador reiniciado)',
        category: 'Administración',
        icon: Filter,
        path: '/duplicated-sales',
        keywords: ['venta', 'duplicada', 'duplicated', 'duplicado', 'perdida', 'admin']
      });
    }
    if (canAccess(role, type, category, 'tiempos')) {
      commands.push({
        id: 'tiempos',
        label: 'Tiempos',
        description: 'Tiempos de operación por venta',
        category: 'Reporte',
        icon: Timer,
        path: '/tiempos',
        keywords: ['tiempo', 'operacion', 'duracion', 'time', 'kpi']
      });
    }
    if (canAccess(role, type, category, 'internalsales')) {
      commands.push({
        id: 'internalsales',
        label: 'Análisis por tipo',
        description: 'Análisis de ventas por tipo',
        category: 'Reporte',
        icon: Star,
        path: '/internalsales',
        keywords: ['analisis', 'tipo', 'internal', 'sale']
      });
    }
    if (canAccess(role, type, category, 'arqueos')) {
      commands.push({
        id: 'arqueos',
        label: 'Gráficos',
        description: 'Gráficos de arqueos',
        category: 'Reporte',
        icon: BarChart2,
        path: '/arqueos',
        keywords: ['grafico', 'arqueo', 'chart']
      });
      commands.push({
        id: 'arqueossales',
        label: 'KPI',
        description: 'Indicadores clave de rendimiento',
        category: 'Reporte',
        icon: PieChart,
        path: '/arqueossales',
        keywords: ['kpi', 'indicador', 'metric']
      });
    }
    if (canAccess(role, type, category, 'withdrawals')) {
      commands.push({
        id: 'withdrawals',
        label: 'Retiros',
        description: 'Gestión de retiros',
        category: 'Reporte',
        icon: Calculator,
        path: '/withdrawals',
        keywords: ['retiro', 'withdrawal', 'cash']
      });
    }
    if (canAccess(role, type, category, 'api')) {
      commands.push({
        id: 'api',
        label: 'API',
        description: 'Exportación automática a ERP/SAP',
        category: 'Reporte',
        icon: Code,
        path: '/api',
        keywords: ['api', 'erp', 'sap', 'exportar', 'integracion', 'automatico']
      });
    }
    if (canAccess(role, type, category, 'ocupation')) {
      commands.push({
        id: 'ocupation',
        label: 'Ocupación',
        description: 'Reporte de ocupación',
        category: 'Reporte',
        icon: BarChart2,
        path: '/ocupation',
        keywords: ['ocupacion', 'occupancy', 'uso']
      });
    }
    if (canAccess(role, type, category, 'objectives')) {
      commands.push({
        id: 'objectives',
        label: 'Objetivos',
        description: 'Gestión de objetivos',
        category: 'Reporte',
        icon: Target,
        path: '/objectives',
        keywords: ['objetivo', 'target', 'meta']
      });
    }
    if (canAccess(role, type, category, 'localizacion')) {
      commands.push({
        id: 'localizacion',
        label: 'Localización',
        description: 'Mapa de localización de kioscos',
        category: 'Reporte',
        icon: MapPin,
        path: '/localizacion',
        keywords: ['localizacion', 'mapa', 'ubicacion', 'map']
      });
    }
    if (canAccess(role, type, category, 'operational-dashboard')) {
      commands.push({
        id: 'operational-dashboard',
        label: 'Dashboard Operativo',
        description: 'Panel de control operativo',
        category: 'Reporte',
        icon: BarChart2,
        path: '/operational-dashboard',
        keywords: ['dashboard', 'operativo', 'panel', 'control']
      });
    }
    if (canAccess(role, type, category, 'occupancy-dashboard')) {
      commands.push({
        id: 'occupancy-dashboard',
        label: 'Dashboard Ocupación',
        description: 'Panel de control de ocupación',
        category: 'Reporte',
        icon: PieChart,
        path: '/occupancy-dashboard',
        keywords: ['dashboard', 'ocupacion', 'panel']
      });
    }
    if (canAccess(role, type, category, 'live-occupancy')) {
      commands.push({
        id: 'live-occupancy',
        label: 'Ocupación en vivo',
        description: 'Estado en tiempo real de las taquillas',
        category: 'Reporte',
        icon: DoorOpen,
        path: '/live-occupancy',
        keywords: ['ocupacion', 'vivo', 'tiempo real', 'live', 'occupancy', 'llenado']
      });
    }
    if (canAccess(role, type, category, 'ocupationaccumulated')) {
      commands.push({
        id: 'ocupationaccumulated',
        label: 'Ocupación acumulada',
        description: 'Reporte de ocupación acumulada',
        category: 'Baqueira',
        icon: BarChart2,
        path: '/ocupationaccumulated',
        keywords: ['ocupacion', 'acumulada', 'accumulated']
      });
      commands.push({
        id: 'ocupationdaily',
        label: 'Ocupación diaria',
        description: 'Reporte de ocupación diaria',
        category: 'Baqueira',
        icon: BarChart2,
        path: '/ocupationdaily',
        keywords: ['ocupacion', 'diaria', 'daily']
      });
    }

    // Operativa
    if (canAccess(role, type, category, 'supporttickets')) {
      commands.push({
        id: 'supporttickets',
        label: 'Tickets',
        description: 'Lista de tickets de soporte',
        category: 'Operativa',
        icon: Receipt,
        path: '/supporttickets',
        keywords: ['ticket', 'soporte', 'support']
      });
    }
    if (canAccess(role, type, category, 'faults')) {
      commands.push({
        id: 'faults',
        label: 'Averías',
        description: 'Gestión de averías',
        category: 'Operativa',
        icon: Info,
        path: '/faults',
        keywords: ['averia', 'fault', 'problema']
      });
    }
    if (canAccess(role, type, category, 'incidences')) {
      commands.push({
        id: 'incidences',
        label: 'Incidencias',
        description: 'Gestión de incidencias',
        category: 'Operativa',
        icon: SlidersHorizontal,
        path: '/incidences',
        keywords: ['incidencia', 'incident', 'issue']
      });
    }
    if (canAccess(role, type, category, 'audit')) {
      commands.push({
        id: 'audit',
        label: 'Audit',
        description: 'Auditoría del sistema',
        category: 'Operativa',
        icon: Database,
        path: '/audit',
        keywords: ['audit', 'auditoria', 'log']
      });
    }
    if (canAccess(role, type, category, 'tpvs')) {
      commands.push({
        id: 'tpvs',
        label: 'TPVs',
        description: 'Gestión de terminales de pago',
        category: 'Operativa',
        icon: Smartphone,
        path: '/tpvs',
        keywords: ['tpv', 'terminal', 'payment']
      });
    }
    if (canAccess(role, type, category, 'modules')) {
      commands.push({
        id: 'modules',
        label: 'Módulos',
        description: 'Gestión de módulos',
        category: 'Operativa',
        icon: LayoutGrid,
        path: '/modules',
        keywords: ['modulo', 'module', 'componente']
      });
    }
    if (canAccess(role, type, category, 'matrixs')) {
      commands.push({
        id: 'matrixs',
        label: 'Firmware',
        description: 'Control de versiones',
        category: 'Operativa',
        icon: Download,
        path: '/matrixs',
        keywords: ['version', 'matrix', 'firmware']
      });
    }
    if (canAccess(role, type, category, 'lowbluetooth')) {
      commands.push({
        id: 'lowbluetooth',
        label: 'Bluetooth',
        description: 'Estado de conexiones Bluetooth',
        category: 'Operativa',
        icon: Wifi,
        path: '/lowbluetooth',
        keywords: ['bluetooth', 'conexion', 'wireless']
      });
    }
    if (canAccess(role, type, category, 'lockersqty')) {
      commands.push({
        id: 'lockersqty',
        label: 'Lockers QTY',
        description: 'Cantidad de lockers',
        category: 'Operativa',
        icon: BarChart2,
        path: '/lockersqty',
        keywords: ['locker', 'cantidad', 'quantity']
      });
    }
    if (canAccess(role, type, category, 'openings')) {
      commands.push({
        id: 'openings',
        label: 'Aperturas',
        description: 'Registro de aperturas',
        category: 'Operativa',
        icon: DoorOpen,
        path: '/openings',
        keywords: ['apertura', 'opening', 'unlock']
      });
    }
    if (canAccess(role, type, category, 'pinrecovers')) {
      commands.push({
        id: 'pinrecovers',
        label: 'Recuperación PINes',
        description: 'Historial de recuperación de PINes',
        category: 'Reporte',
        icon: Lock,
        path: '/pinrecovers',
        keywords: ['pin', 'recuperacion', 'recovery']
      });
    }
    if (canAccess(role, type, category, 'rentals')) {
      commands.push({
        id: 'rentals',
        label: 'Reservas',
        description: 'Gestión de reservas',
        category: 'Gestión',
        icon: Hotel,
        path: '/rentals',
        keywords: ['reserva', 'rental', 'booking']
      });
    }
    if (canAccess(role, type, category, 'calendars')) {
      commands.push({
        id: 'calendars',
        label: 'Calendarios',
        description: 'Gestión de calendarios',
        category: 'Gestión',
        icon: CalendarCheck2,
        path: '/calendars',
        keywords: ['calendario', 'calendar', 'fecha']
      });
    }
    if (canAccess(role, type, category, 'rules')) {
      commands.push({
        id: 'rules',
        label: 'Reglas de negocio',
        description: 'Configuración de reglas de negocio',
        category: 'Baqueira',
        icon: PencilRuler,
        path: '/rules',
        keywords: ['regla', 'rule', 'negocio', 'business']
      });
    }
    if (canAccess(role, type, category, 'articles')) {
      commands.push({
        id: 'articles',
        label: 'Artículos',
        description: 'Gestión de artículos',
        category: 'Baqueira',
        icon: Newspaper,
        path: '/articles',
        keywords: ['articulo', 'article', 'producto']
      });
    }
    if (canAccess(role, type, category, 'arqueosbaqueira')) {
      commands.push({
        id: 'arqueosbaqueira',
        label: 'Arqueos ERP Baqueira',
        description: 'Arqueos del sistema ERP Baqueira',
        category: 'Baqueira',
        icon: Gem,
        path: '/arqueosbaqueira',
        keywords: ['arqueo', 'baqueira', 'erp']
      });
    }

    // Soporte
    if (canAccess(role, type, category, 'support-chat')) {
      commands.push({
        id: 'support-chat',
        label: 'Chat de Soporte',
        description: 'Chat técnico de soporte',
        category: 'Soporte',
        icon: MessageSquare,
        path: '/support-chat',
        keywords: ['chat', 'soporte', 'mensajes', 'support']
      });
    }
    if (canAccess(role, type, category, 'integraciones')) {
      commands.push({
        id: 'integraciones',
        label: 'Integraciones',
        description: 'Documentación técnica de la API',
        category: 'Soporte',
        icon: Code,
        path: '/integraciones',
        keywords: ['api', 'documentacion', 'docs', 'developer', 'integraciones']
      });
    }
    if (canAccess(role, type, category, 'planes')) {
      commands.push({
        id: 'planes',
        label: 'Planes',
        description: 'Comparativa de licencias BÁSICA y PRO',
        category: 'Soporte',
        icon: Star,
        path: '/planes',
        keywords: ['plan', 'licencia', 'pro', 'basica', 'upgrade', 'actualizar']
      });
    }
    if (canAccess(role, type, category, 'faqs')) {
      commands.push({
        id: 'faqs',
        label: 'FAQs',
        description: 'Preguntas frecuentes',
        category: 'Soporte',
        icon: HelpCircle,
        path: '/faqs',
        keywords: ['faq', 'pregunta', 'ayuda', 'help']
      });
      commands.push({
        id: 'manuals',
        label: 'Manuales',
        description: 'Manuales de usuario',
        category: 'Soporte',
        icon: BookOpen,
        path: '/manuals',
        keywords: ['manual', 'guia', 'documentacion']
      });
    }

    // Admin
    if (canAccess(role, type, category, 'customers')) {
      commands.push({
        id: 'customers',
        label: 'Clientes',
        description: 'Gestión de clientes',
        category: 'Administración',
        icon: Crown,
        path: '/customers',
        keywords: ['cliente', 'customer', 'empresa']
      });
    }
    if (canAccess(role, type, category, 'contracts')) {
      commands.push({
        id: 'contracts',
        label: 'Contratos',
        description: 'Gestión de contratos',
        category: 'Administración',
        icon: FileSignature,
        path: '/contracts',
        keywords: ['contrato', 'contract', 'acuerdo']
      });
    }
    if (canAccess(role, type, category, 'gameranking')) {
      commands.push({
        id: 'gameranking',
        label: 'Juegos',
        description: 'Ranking de juegos',
        category: 'Administración',
        icon: Gamepad2,
        path: '/gameranking',
        keywords: ['juego', 'game', 'ranking']
      });
    }
    if (canAccess(role, type, category, 'replacements')) {
      commands.push({
        id: 'replacements',
        label: 'Recambios',
        description: 'Gestión de recambios',
        category: 'Administración',
        icon: SlidersHorizontal,
        path: '/replacements',
        keywords: ['recambio', 'replacement', 'repuesto']
      });
    }
    if (canAccess(role, type, category, 'users')) {
      commands.push({
        id: 'users',
        label: 'Administradores',
        description: 'Gestión de usuarios administradores',
        category: 'Administración',
        icon: UserCog,
        path: '/users',
        keywords: ['usuario', 'user', 'admin', 'administrador']
      });
    }
    if (canAccess(role, type, category, 'slas')) {
      commands.push({
        id: 'slas',
        label: 'SLA',
        description: 'Acuerdos de nivel de servicio',
        category: 'Operativa',
        icon: SlidersHorizontal,
        path: '/slas',
        keywords: ['sla', 'acuerdo', 'servicio', 'nivel']
      });
    }
    if (canAccess(role, type, category, 'comunicaciones')) {
      commands.push({
        id: 'comunicaciones',
        label: 'Comunicaciones',
        description: 'Emails de notificación de tickets e incidencias',
        category: 'SAT',
        icon: Mail,
        path: '/comunicaciones',
        keywords: ['comunicaciones', 'email', 'notificacion', 'ticket', 'incidencia']
      });
    }

    // Usuarios
    if (canAccess(role, type, category, 'my-cash-users')) {
      commands.push({
        id: 'my-cash-users',
        label: 'Usuarios Cash-Control',
        description: 'Gestión de usuarios Cash-Control',
        category: 'Usuarios',
        icon: UserCog,
        path: '/my-cash-users',
        keywords: ['usuario', 'cash', 'control', 'user']
      });
    }
    if (canAccess(role, type, category, 'my-pda-users')) {
      commands.push({
        id: 'my-pda-users',
        label: 'Usuarios Operativo',
        description: 'Gestión de usuarios Operativo (PDA)',
        category: 'Usuarios',
        icon: UserCog,
        path: '/my-pda-users',
        keywords: ['usuario', 'pda', 'operativo', 'user']
      });
    }

    return commands;
  }, [role, type, category, canAccess]);

  // Filter commands based on search
  const filteredCommands = useMemo(() => {
    if (!search.trim()) return allCommands;
    
    const searchLower = search.toLowerCase();
    return allCommands.filter(cmd => {
      const matchLabel = cmd.label.toLowerCase().includes(searchLower);
      const matchDescription = cmd.description.toLowerCase().includes(searchLower);
      const matchCategory = cmd.category.toLowerCase().includes(searchLower);
      const matchKeywords = cmd.keywords.some(k => k.includes(searchLower));
      
      return matchLabel || matchDescription || matchCategory || matchKeywords;
    });
  }, [search, allCommands]);

  // Group commands by category
  const groupedCommands = useMemo(() => {
    const groups = {};
    filteredCommands.forEach(cmd => {
      if (!groups[cmd.category]) {
        groups[cmd.category] = [];
      }
      groups[cmd.category].push(cmd);
    });
    return groups;
  }, [filteredCommands]);

  // Reset selection when filtered commands change
  useEffect(() => {
    setSelectedIndex(0);
  }, [filteredCommands]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  // Handle keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
      } else if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex(prev => 
          prev < filteredCommands.length - 1 ? prev + 1 : prev
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : prev));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (filteredCommands[selectedIndex]) {
          handleNavigate(filteredCommands[selectedIndex].path);
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, selectedIndex, filteredCommands, onClose]);

  const handleNavigate = (path) => {
    const feature = path.replace(/^\//, '');
    if (isFeatureLocked(role, type, category, feature)) {
      setProToast(true);
      setTimeout(() => setProToast(false), 3500);
      return;
    }
    push(path);
    onClose();
    setSearch("");
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div 
        className="command-palette-backdrop"
        onClick={onClose}
      />

      {/* Palette */}
      <div className="command-palette">
        <div className="command-palette-header">
          <Search size={20} className="command-palette-search-icon" />
          <input
            ref={inputRef}
            type="text"
            className="command-palette-input"
            placeholder="Buscar acciones, páginas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            aria-label="Buscar comando"
          />
          <kbd className="command-palette-kbd">ESC</kbd>
        </div>

        <div className="command-palette-content">
          {filteredCommands.length === 0 ? (
            <div className="command-palette-empty">
              No se encontraron resultados para "{search}"
            </div>
          ) : (
            Object.entries(groupedCommands).map(([category, commands]) => (
              <div key={category} className="command-palette-group">
                <div className="command-palette-group-label">{category}</div>
                {commands.map((cmd, index) => {
                  const globalIndex = filteredCommands.indexOf(cmd);
                  const Icon = cmd.icon;
                  return (
                    <button
                      key={cmd.id}
                      className={`command-palette-item ${
                        globalIndex === selectedIndex ? "selected" : ""
                      } ${isFeatureLocked(role, type, category, cmd.path.replace(/^\//, '')) ? "command-palette-item--locked" : ""}`}
                      onClick={() => handleNavigate(cmd.path)}
                      onMouseEnter={() => setSelectedIndex(globalIndex)}
                    >
                      <div className="command-palette-item-left">
                        <Icon size={18} className="command-palette-item-icon" />
                        <div className="command-palette-item-content">
                          <div className="command-palette-item-label">
                            {cmd.label}
                            {isFeatureLocked(role, type, category, cmd.path.replace(/^\//, '')) && (
                              <span className="pro-badge-cmd">PRO</span>
                            )}
                          </div>
                          <div className="command-palette-item-description">
                            {cmd.description}
                          </div>
                        </div>
                      </div>
                      {isFeatureLocked(role, type, category, cmd.path.replace(/^\//, '')) 
                        ? <Lock size={14} style={{ opacity: 0.4 }} />
                        : <ArrowRight size={16} className="command-palette-item-arrow" />
                      }
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="command-palette-footer">
          <div className="command-palette-footer-item">
            <kbd>↑</kbd><kbd>↓</kbd> para navegar
          </div>
          <div className="command-palette-footer-item">
            <kbd>↵</kbd> para seleccionar
          </div>
          <div className="command-palette-footer-item">
            <kbd>ESC</kbd> para cerrar
          </div>
        </div>

        {proToast && (
          <div className="pro-toast" style={{ position: 'absolute', bottom: 60, left: '50%', transform: 'translateX(-50%)' }}>
            <Lock size={16} />
            <span>Esta función solo está disponible con la <strong>Licencia Pro</strong></span>
          </div>
        )}
      </div>
    </>
  );
};

export default CommandPalette;
